// SMS Template Service for handling template persistence
class SMSTemplateService {
  constructor() {
    this.storageKey = 'sms_templates';
  }

  // Get all templates
  getAllTemplates() {
    try {
      const templates = localStorage.getItem(this.storageKey);
      return templates ? JSON.parse(templates) : [];
    } catch (error) {
      console.error('Error loading templates:', error);
      return [];
    }
  }

  // Save a template
  saveTemplate(template) {
    try {
      const templates = this.getAllTemplates();
      const existingIndex = templates.findIndex(t => t.id === template.id);
      
      if (existingIndex >= 0) {
        templates[existingIndex] = { ...template, updatedAt: new Date().toISOString() };
      } else {
        templates.push({ 
          ...template, 
          id: template.id || Date.now(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }
      
      localStorage.setItem(this.storageKey, JSON.stringify(templates));
      return true;
    } catch (error) {
      console.error('Error saving template:', error);
      return false;
    }
  }

  // Delete a template
  deleteTemplate(templateId) {
    try {
      const templates = this.getAllTemplates();
      const filteredTemplates = templates.filter(t => t.id !== templateId);
      localStorage.setItem(this.storageKey, JSON.stringify(filteredTemplates));
      return true;
    } catch (error) {
      console.error('Error deleting template:', error);
      return false;
    }
  }

  // Get template by ID
  getTemplateById(templateId) {
    const templates = this.getAllTemplates();
    return templates.find(t => t.id === templateId);
  }

  // Export template as JSON
  exportTemplate(templateId) {
    const template = this.getTemplateById(templateId);
    if (!template) return null;
    
    const dataStr = JSON.stringify(template, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `sms-template-${template.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    return true;
  }

  // Import template from JSON
  importTemplate(jsonString) {
    try {
      const template = JSON.parse(jsonString);
      
      // Validate template structure
      if (!template.name || !Array.isArray(template.stages)) {
        throw new Error('Invalid template format');
      }
      
      // Assign new ID to avoid conflicts
      template.id = Date.now();
      template.importedAt = new Date().toISOString();
      
      return this.saveTemplate(template) ? template : null;
    } catch (error) {
      console.error('Error importing template:', error);
      return null;
    }
  }

  // Validate template structure
  validateTemplate(template) {
    const errors = [];
    
    if (!template.name || template.name.trim() === '') {
      errors.push('Template name is required');
    }
    
    if (!template.stages || template.stages.length === 0) {
      errors.push('At least one stage is required');
    }
    
    if (template.stages) {
      const hasInitialStage = template.stages.some(stage => stage.isInitial);
      if (!hasInitialStage) {
        errors.push('Template must have an initial stage');
      }
      
      template.stages.forEach((stage, index) => {
        if (!stage.question || stage.question.trim() === '') {
          errors.push(`Stage ${index + 1} must have a question`);
        }
        
        stage.answers?.forEach((answer, answerIndex) => {
          if (!answer.text || answer.text.trim() === '') {
            errors.push(`Stage ${index + 1}, Answer ${answerIndex + 1} must have text`);
          }
        });
      });
    }
    
    return errors;
  }

  // Generate conversation flow diagram data
  generateFlowDiagram(template) {
    const nodes = [];
    const edges = [];
    
    template.stages.forEach(stage => {
      nodes.push({
        id: stage.id.toString(),
        type: stage.isInitial ? 'initial' : 'stage',
        data: { 
          label: stage.question,
          isInitial: stage.isInitial 
        }
      });
      
      stage.answers?.forEach(answer => {
        if (answer.nextStageId) {
          edges.push({
            id: `${stage.id}-${answer.nextStageId}`,
            source: stage.id.toString(),
            target: answer.nextStageId.toString(),
            label: answer.text,
            type: 'smoothstep'
          });
        }
      });
    });
    
    return { nodes, edges };
  }
}

export default new SMSTemplateService();